import { ReactNode, useEffect, useRef, useState } from "react";
import { Box, Container, Typography } from "@mui/material";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/analytics";
import { fontStack } from "@/theme";
import AuthForm from "./AuthForm";
import ResetPasswordDialog from "./ResetPasswordDialog";

interface AuthGateProps {
  /** Feature id — `feed`, `multicam`, etc. Drives GA attribution. */
  source: string;
  /** Serif headline on the replacement screen. */
  title: string;
  /** Sans subhead under the headline. */
  reason: string;
  /** Rendered when the user is authed. */
  children: ReactNode;
}

/**
 * Route-level auth gate. When the visitor is signed in, renders
 * `children`. When anon, renders a full-container replacement with a
 * full `AuthForm` (email/password + Google) so users without a Google
 * account can still sign up. Children are not mounted — matters for
 * pages like multi-cam where the children would otherwise spin up
 * video streams the user can't watch.
 */
export default function AuthGate({ source, title, reason, children }: AuthGateProps) {
  const { user, loading } = useAuth();

  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  // Fire `auth_gate_shown` once per anon mount so GA4 can split by
  // source and measure gate → signin conversion.
  const loggedRef = useRef(false);
  useEffect(() => {
    if (loading || user || loggedRef.current) return;
    loggedRef.current = true;
    trackEvent("auth_gate_shown", { source });
  }, [user, loading, source]);

  if (loading) return null;
  if (user) return <>{children}</>;

  return (
    <Box sx={{ minHeight: "60vh", display: "flex", alignItems: "center" }}>
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography
          sx={{
            fontFamily: fontStack.mono,
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "ink.muted",
            mb: 3,
            textAlign: "center",
          }}
        >
          Sign in required
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: fontStack.serif,
            fontWeight: 500,
            fontSize: { xs: 32, md: 44 },
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            color: "ink.main",
            mb: 2,
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: fontStack.sans,
            fontSize: 16,
            lineHeight: 1.5,
            color: "ink.muted",
            mb: 4,
            maxWidth: 440,
            mx: "auto",
            textAlign: "center",
          }}
        >
          {reason}
        </Typography>
        <Box sx={{ maxWidth: 380, mx: "auto" }}>
          <AuthForm
            source={source}
            onForgotPassword={(email) => {
              setResetEmail(email);
              setResetOpen(true);
            }}
          />
        </Box>
      </Container>
      <ResetPasswordDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        initialEmail={resetEmail}
        source={source}
      />
    </Box>
  );
}
